import { useState, useEffect, useCallback } from 'react';
import { api } from '@/api/client';
import type { Vacancy } from '@/types';
import GeneratedQuestionsModal from './components/GeneratedQuestionsModal';

interface Competency {
  id: string;
  name: string;
  weight: number;
  description: string;
}

interface Criterion {
  id: string;
  name: string;
  weight: number;
  description: string;
  competency_id?: string | null;
}

interface Question {
  id: string;
  name: string;
  weight: number;
  question: string;
  eval_criteria: string;
  competency_id?: string | null;
  is_compatibility?: boolean;
}

export default function Scoring() {
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [vacancyId, setVacancyId] = useState<string | null>(null);
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [suggestingComp, setSuggestingComp] = useState(false);
  const [questionForComp, setQuestionForComp] = useState<string | null>(null);
  // Сгенерированные вопросы сначала показываем на утверждение, а не добавляем молча
  const [draftQuestions, setDraftQuestions] = useState<Question[] | null>(null);
  const [resumeCriteria, setResumeCriteria] = useState<Criterion[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [activeTab, setActiveTab] = useState<'skills' | 'resume' | 'questions'>('skills');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [reworkingId, setReworkingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadVacancy = useCallback((v: Vacancy) => {
    setCompetencies(((v as unknown as { competencies?: Competency[] }).competencies || []) as Competency[]);
    setResumeCriteria((v.resume_criteria || []) as Criterion[]);
    setQuestions((v.question_criteria || []) as Question[]);
  }, []);

  useEffect(() => {
    api.get<Vacancy[]>('/vacancies').then((data) => {
      setVacancies(data);
      if (data.length > 0) {
        const active = data.find((v) => v.status === 'active') || data[0];
        setVacancyId(active.id);
        loadVacancy(active);
      }
    }).finally(() => setLoading(false));
  }, [loadVacancy]);

  const handleVacancyChange = (id: string) => {
    setVacancyId(id);
    const v = vacancies.find((x) => x.id === id);
    if (v) loadVacancy(v);
  };

  const totalCompetencyWeight = competencies.reduce((s, c) => s + (Number(c.weight) || 0), 0);
  const totalResumeWeight = resumeCriteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);
  // Покрытие: какими критериями резюме и вопросами закрыт каждый навык
  const coverage = competencies.map((comp) => ({
    comp,
    criteria: resumeCriteria.filter((c) => c.competency_id === comp.id),
    questions: questions.filter((q) => q.competency_id === comp.id),
  }));
  const uncovered = coverage.filter((c) => c.criteria.length === 0 && c.questions.length === 0);
  const competencyName = (id?: string | null) => competencies.find((c) => c.id === id)?.name || '';
  const totalQuestionWeight = questions.reduce((s, q) => s + (Number(q.weight) || 0), 0);

  const updateCriterion = (id: string, field: keyof Criterion, value: string | number | null) => {
    setResumeCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const updateCompetency = (id: string, field: keyof Competency, value: string | number) => {
    setCompetencies((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const addCompetency = () => {
    const id = `comp_${Date.now().toString(36)}`;
    setCompetencies((prev) => [...prev, { id, name: 'Новый навык', weight: 10, description: '' }]);
  };

  const removeCompetency = (id: string) => {
    setCompetencies((prev) => prev.filter((c) => c.id !== id));
    setResumeCriteria((prev) => prev.map((c) => (c.competency_id === id ? { ...c, competency_id: null } : c)));
    setQuestions((prev) => prev.map((q) => (q.competency_id === id ? { ...q, competency_id: null } : q)));
  };

  const handleSuggestCompetencies = async () => {
    if (!vacancyId || suggestingComp) return;
    if (competencies.length > 0 && !window.confirm('Список навыков будет заменён предложением AI. Продолжить?')) return;
    setSuggestingComp(true);
    try {
      const data = await api.post<{ competencies: Competency[] }>(`/vacancies/${vacancyId}/suggest-competencies`);
      if (data.competencies?.length) setCompetencies(data.competencies);
    } catch (err) {
      alert((err as Error).message || 'Ошибка AI-подбора навыков');
    }
    setSuggestingComp(false);
  };

  const updateQuestion = (id: string, field: keyof Question, value: string | number | boolean | null) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, [field]: value } : q)));
  };

  const addCriterion = () => {
    const id = `crit_${Date.now().toString(36)}`;
    setResumeCriteria((prev) => [...prev, { id, name: 'Новый критерий', weight: 5, description: '' }]);
    setEditingId(id);
  };

  const addQuestion = () => {
    const id = `q_${Date.now().toString(36)}`;
    setQuestions((prev) => [...prev, { id, name: 'Новый вопрос', weight: 10, question: '', eval_criteria: '' }]);
  };

  const removeCriterion = (id: string) => setResumeCriteria((prev) => prev.filter((c) => c.id !== id));
  const removeQuestion = (id: string) => setQuestions((prev) => prev.filter((q) => q.id !== id));

  const moveQuestion = (id: string, dir: number) => {
    setQuestions((prev) => {
      const i = prev.findIndex((q) => q.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const handleSave = async () => {
    if (!vacancyId) return;
    setSaving(true);
    try {
      const updated = await api.put<Vacancy>(`/vacancies/${vacancyId}/criteria`, {
        competencies,
        resume_criteria: resumeCriteria,
        question_criteria: questions,
      });
      setVacancies((prev) => prev.map((v) => (v.id === vacancyId ? {
        ...v,
        competencies: (updated as unknown as { competencies?: Competency[] }).competencies ?? competencies,
        resume_criteria: updated.resume_criteria,
        question_criteria: updated.question_criteria,
      } : v)));
      setSavedAt(true);
      setTimeout(() => setSavedAt(false), 2500);
    } catch (err) {
      alert((err as Error).message || 'Ошибка сохранения');
    }
    setSaving(false);
  };

  const handleAIGenerateQuestions = async () => {
    if (!vacancyId || generating) return;
    const focus = window.prompt(
      'Что должны выявлять новые вопросы? (можно оставить пустым — AI решит сам по вакансии и портрету руководителя)',
      ''
    );
    if (focus === null) return;
    setGenerating(true);
    try {
      const data = await api.post<{ questions: Question[] }>(`/vacancies/${vacancyId}/suggest-questions`, {
        count: 3,
        focus: focus || null,
      });
      if (data.questions?.length) setDraftQuestions(data.questions);
    } catch (err) {
      alert((err as Error).message || 'Ошибка AI-генерации');
    }
    setGenerating(false);
  };

  const handleQuestionForCompetency = async (comp: Competency) => {
    if (!vacancyId || questionForComp) return;
    setQuestionForComp(comp.id);
    try {
      const focus =
        `Придумай вопрос, который КОСВЕННО проверяет навык «${comp.name}»` +
        (comp.description ? ` (${comp.description})` : '') +
        '. Не спрашивай про качество напрямую и не используй его название в тексте вопроса — ' +
        'спроси про конкретный случай из прошлого опыта или дай жизненную ситуацию с этой работы, ' +
        'по ответу на которую видно, есть навык или нет.';
      const data = await api.post<{ questions: Question[] }>(`/vacancies/${vacancyId}/suggest-questions`, {
        count: 1,
        focus,
      });
      const nq = data.questions?.[0];
      if (nq) setDraftQuestions([{ ...nq, competency_id: comp.id }]);
    } catch (err) {
      alert((err as Error).message || 'Ошибка AI-генерации');
    }
    setQuestionForComp(null);
  };

  const handleReworkQuestion = async (q: Question) => {
    if (!vacancyId || reworkingId) return;
    const feedback = window.prompt('Что изменить в этом вопросе? (например: «короче и конкретнее», «убери часть про английский»)', '');
    if (feedback === null || !feedback.trim()) return;
    setReworkingId(q.id);
    try {
      const focus = `Перепиши существующий вопрос анкеты с учётом правок HR. НЕ придумывай вопрос на другую тему — сохрани суть, внеси правки.\nИсходный вопрос: «${q.question || q.name}»\nПравки HR: ${feedback}`;
      const data = await api.post<{ questions: Question[] }>(`/vacancies/${vacancyId}/suggest-questions`, { count: 1, focus });
      const nq = data.questions?.[0];
      if (nq) {
        setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, name: nq.name, question: nq.question, eval_criteria: nq.eval_criteria } : x)));
      }
    } catch (err) {
      alert((err as Error).message || 'Ошибка AI-генерации');
    }
    setReworkingId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            Критерии оценки
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            Навыки, критерии резюме и вопросы анкеты — из них складывается оценка кандидата
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={vacancyId || ''}
            onChange={(e) => handleVacancyChange(e.target.value)}
            className="px-4 py-2.5 rounded-xl bg-white border border-background-200 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {vacancies.map((v) => (
              <option key={v.id} value={v.id}>{v.title}</option>
            ))}
          </select>
          {savedAt && (
            <span className="text-xs font-medium text-emerald-600 flex items-center gap-1">
              <i className="ri-check-line" /> Сохранено
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-sm flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
            Сохранить
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-background-200">
        <button
          onClick={() => setActiveTab('skills')}
          className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'skills'
              ? 'border-primary-500 text-primary-700'
              : 'border-transparent text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-focus-3-line" />
          Навыки
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
            {competencies.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('resume')}
          className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'resume'
              ? 'border-primary-500 text-primary-700'
              : 'border-transparent text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-file-list-line" />
          Критерии резюме
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
            {resumeCriteria.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('questions')}
          className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'questions'
              ? 'border-primary-500 text-primary-700'
              : 'border-transparent text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-questionnaire-line" />
          Вопросы анкеты
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
            {questions.length}
          </span>
        </button>
      </div>

      {/* Skills Tab */}
      {activeTab === 'skills' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-base font-heading font-semibold text-foreground-900">
                Навыки: {vacancies.find((v) => v.id === vacancyId)?.title || 'вакансия не выбрана'}
              </h3>
              <p className="text-xs text-foreground-500 mt-0.5">
                Что именно мы проверяем и насколько это весомо. К навыкам привязываются критерии резюме и вопросы анкеты.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={addCompetency}
                className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" />
                Добавить навык
              </button>
              <button
                onClick={handleSuggestCompetencies}
                disabled={suggestingComp}
                className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent-50 text-accent-700 hover:bg-accent-100 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {suggestingComp ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-sparkling-line" />}
                {suggestingComp ? 'AI думает...' : 'Предложить с помощью AI'}
              </button>
            </div>
          </div>

          {competencies.length > 0 && totalCompetencyWeight !== 100 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-800">
              <i className="ri-alert-line" />
              Сумма весов навыков: {totalCompetencyWeight}%. Должна быть ровно 100%.
            </div>
          )}

          {competencies.length === 0 ? (
            <div className="bg-white rounded-xl border border-background-200 p-8 text-center">
              <i className="ri-focus-3-line text-2xl text-foreground-300" />
              <p className="text-sm text-foreground-500 mt-2">
                Навыки не заданы — оценка кандидатов идёт без явной модели требований.
              </p>
              <p className="text-xs text-foreground-400 mt-1">
                Добавьте вручную или нажмите «Предложить с помощью AI» — навыки соберутся из описания вакансии.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {coverage.map(({ comp, criteria, questions: qs }) => (
                <div key={comp.id} className="bg-white rounded-xl border border-background-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 space-y-2">
                      <input
                        value={comp.name}
                        onChange={(e) => updateCompetency(comp.id, 'name', e.target.value)}
                        className="w-full font-medium text-foreground-900 bg-transparent border-b border-transparent hover:border-background-300 focus:border-primary-400 focus:outline-none transition-colors text-sm py-0.5"
                      />
                      <textarea
                        value={comp.description || ''}
                        onChange={(e) => updateCompetency(comp.id, 'description', e.target.value)}
                        rows={2}
                        placeholder="Что этот навык означает для вас — видит только AI при оценке"
                        className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                      />
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${criteria.length ? 'bg-purple-50 text-purple-700' : 'bg-background-100 text-foreground-400'}`}>
                          критериев резюме: {criteria.length}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full ${qs.length ? 'bg-emerald-50 text-emerald-700' : 'bg-background-100 text-foreground-400'}`}>
                          вопросов анкеты: {qs.length}
                        </span>
                        {criteria.length === 0 && qs.length === 0 && (
                          <span className="text-amber-700">— навык ничем не проверяется</span>
                        )}
                        <button
                          onClick={() => handleQuestionForCompetency(comp)}
                          disabled={questionForComp !== null}
                          className="px-2 py-0.5 rounded-full border border-accent-200 text-accent-700 hover:bg-accent-50 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1"
                        >
                          {questionForComp === comp.id ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-sparkling-line" />
                          )}
                          {questionForComp === comp.id ? 'AI думает...' : 'Придумать вопрос'}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={comp.weight}
                        onChange={(e) => updateCompetency(comp.id, 'weight', Number(e.target.value))}
                        className="w-16 px-2 py-1 rounded bg-background-50 border border-background-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                      <span className="text-xs text-foreground-400">%</span>
                      <button
                        onClick={() => removeCompetency(comp.id)}
                        className="w-7 h-7 rounded hover:bg-red-50 flex items-center justify-center text-foreground-300 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Непокрытые навыки — предупреждение на вкладках критериев и вопросов */}
      {activeTab !== 'skills' && uncovered.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-800">
          <i className="ri-alert-line mt-0.5" />
          <span>
            Не проверяются ни одним вопросом и критерием: {uncovered.map((u) => u.comp.name).join(', ')}
          </span>
        </div>
      )}

      {/* Weight warning */}
      {activeTab === 'resume' && totalResumeWeight !== 100 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-800">
          <i className="ri-alert-line" />
          Сумма весов критериев резюме: {totalResumeWeight}%. Должна быть ровно 100%.
        </div>
      )}
      {activeTab === 'questions' && totalQuestionWeight !== 100 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm text-amber-800">
          <i className="ri-alert-line" />
          Сумма весов вопросов: {totalQuestionWeight}%. Должна быть ровно 100%.
        </div>
      )}

      {/* Resume Criteria Tab */}
      {activeTab === 'resume' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-heading font-semibold text-foreground-900">
              Критерии оценки резюме
            </h3>
            <button
              onClick={addCriterion}
              className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Добавить критерий
            </button>
          </div>

          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-50">
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-12">#</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Название критерия</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-24">Вес</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-44">Навык</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Описание (что искать — видит только AI)</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {resumeCriteria.map((c, i) => (
                    <tr key={c.id} className="border-b border-background-100 last:border-0">
                      <td className="px-4 py-3 text-xs text-foreground-400">{i + 1}</td>
                      <td className="px-4 py-3">
                        {editingId === c.id ? (
                          <input
                            value={c.name}
                            onChange={(e) => updateCriterion(c.id, 'name', e.target.value)}
                            className="w-full px-2 py-1 rounded bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        ) : (
                          <span className="font-medium text-foreground-900">{c.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={c.weight}
                            onChange={(e) => updateCriterion(c.id, 'weight', Number(e.target.value))}
                            className="w-16 px-2 py-1 rounded bg-background-50 border border-background-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                          <span className="text-xs text-foreground-400">%</span>
                        </div>
                        <div className="mt-1 h-1 rounded-full overflow-hidden bg-purple-100">
                          <div
                            className="h-full rounded-full bg-purple-500 transition-all"
                            style={{ width: `${Math.min(c.weight, 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={c.competency_id || ''}
                          onChange={(e) => updateCriterion(c.id, 'competency_id', e.target.value)}
                          className="w-full px-2 py-1 rounded bg-background-50 border border-background-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                        >
                          <option value="">— не привязан —</option>
                          {competencies.map((comp) => (
                            <option key={comp.id} value={comp.id}>{comp.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {editingId === c.id ? (
                          <input
                            value={c.description}
                            onChange={(e) => updateCriterion(c.id, 'description', e.target.value)}
                            className="w-full px-2 py-1 rounded bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        ) : (
                          <span className="text-foreground-600">{c.description}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditingId(editingId === c.id ? null : c.id)}
                          className="w-8 h-8 rounded-lg hover:bg-background-100 inline-flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                        >
                          <i className={editingId === c.id ? 'ri-check-line' : 'ri-pencil-line'} />
                        </button>
                        <button
                          onClick={() => removeCriterion(c.id)}
                          className="w-8 h-8 rounded-lg hover:bg-red-50 inline-flex items-center justify-center text-foreground-300 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-heading font-semibold text-foreground-900">
              Вопросы анкеты кандидата
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={addQuestion}
                className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" />
                Новый вопрос
              </button>
              <button
                onClick={handleAIGenerateQuestions}
                disabled={generating}
                className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent-50 text-accent-700 hover:bg-accent-100 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {generating ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-sparkling-line" />}
                {generating ? 'AI думает...' : 'AI-генерация вопросов'}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {questions.map((q, i) => (
              <div key={q.id} className="bg-white rounded-xl border border-background-200 p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center shrink-0 text-xs font-bold text-primary-700">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <input
                        value={q.name}
                        onChange={(e) => updateQuestion(q.id, 'name', e.target.value)}
                        className="w-full font-medium text-foreground-900 bg-transparent border-b border-transparent hover:border-background-300 focus:border-primary-400 focus:outline-none transition-colors text-sm py-0.5"
                      />
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {q.competency_id && competencyName(q.competency_id) && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700">
                            {competencyName(q.competency_id)}
                          </span>
                        )}
                        {q.is_compatibility && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">
                            совместимость
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => moveQuestion(q.id, -1)} className="w-7 h-7 rounded hover:bg-background-100 flex items-center justify-center text-foreground-300 hover:text-foreground-600 cursor-pointer"><i className="ri-arrow-up-s-line" /></button>
                    <button onClick={() => moveQuestion(q.id, 1)} className="w-7 h-7 rounded hover:bg-background-100 flex items-center justify-center text-foreground-300 hover:text-foreground-600 cursor-pointer"><i className="ri-arrow-down-s-line" /></button>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={q.weight}
                      onChange={(e) => updateQuestion(q.id, 'weight', Number(e.target.value))}
                      className="w-14 px-2 py-1 rounded bg-background-50 border border-background-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                    <span className="text-xs text-foreground-400">%</span>
                    <button
                      onClick={() => removeQuestion(q.id)}
                      className="w-7 h-7 rounded hover:bg-red-50 flex items-center justify-center text-foreground-300 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>

                <div className="ml-10 space-y-3">
                  <div>
                    <label className="text-xs text-foreground-500 mb-1 block">Вопрос кандидату (виден на лендинге)</label>
                    <textarea
                      value={q.question}
                      onChange={(e) => updateQuestion(q.id, 'question', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div>
                      <label className="text-xs text-foreground-500 mb-1 block">Какой навык проверяет</label>
                      <select
                        value={q.competency_id || ''}
                        onChange={(e) => updateQuestion(q.id, 'competency_id', e.target.value)}
                        className="px-3 py-1.5 rounded-lg bg-background-50 border border-background-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                      >
                        <option value="">— не привязан —</option>
                        {competencies.map((comp) => (
                          <option key={comp.id} value={comp.id}>{comp.name}</option>
                        ))}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer select-none mt-4">
                      <input
                        type="checkbox"
                        checked={!!q.is_compatibility}
                        onChange={(e) => updateQuestion(q.id, 'is_compatibility', e.target.checked)}
                        className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                      />
                      Вопрос на совместимость с руководителем
                    </label>
                  </div>
                  <div>
                    <label className="text-xs text-foreground-500 mb-1 block">Критерии оценки для AI</label>
                    <textarea
                      value={q.eval_criteria}
                      onChange={(e) => updateQuestion(q.id, 'eval_criteria', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                    />
                  </div>
                  <button
                    onClick={() => handleReworkQuestion(q)}
                    disabled={reworkingId !== null}
                    className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-accent-200 text-accent-700 hover:bg-accent-50 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {reworkingId === q.id ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-sparkling-line" />}
                    {reworkingId === q.id ? 'AI переписывает...' : 'Переформулировать с помощью AI'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {draftQuestions && (
        <GeneratedQuestionsModal
          drafts={draftQuestions}
          competencies={competencies}
          currentWeight={totalQuestionWeight}
          onCancel={() => setDraftQuestions(null)}
          onAdd={(added) => {
            setQuestions((prev) => [...prev, ...added]);
            setDraftQuestions(null);
            setActiveTab('questions');
          }}
        />
      )}
    </div>
  );
}
